import { useGetCampusLinkForCurrentShop } from '@/api/generated/v2-campus-link/v2-campus-link'
import type { CampusLinkMeResponse } from '@/api/schemas'
import { auth, setAuthBootstrapping } from '@/lib/auth'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useEffect } from 'react'

/**
 * /install-not-eligible — 솔루션을 이용할 수 없는 사유를 안내하는 화면.
 *
 * 설치 순서가 바뀌면서(카페24 OAuth 가 맨 앞) 이 페이지의 의미도 바뀌었다.
 * 예전에는 "설치가 차단됐다" 였지만, 이제 설치는 이미 끝난 뒤이므로
 * **"설치는 됐지만 이용할 수 없다"** 를 말한다.
 *
 * no-linked-mall / CampusLinkRequiredScreen 과 동일한 톤 (로고 + 한 단락 본문 +
 * 캠퍼스 브랜드 컬러 CTA).
 */
export const Route = createFileRoute('/install-not-eligible')({
  component: InstallNotEligiblePage,
  validateSearch: (search) => ({
    mall_id: (search.mall_id as string) || '',
    reason: (search.reason as string) || 'not_eligible',
  }),
})

/** 캠퍼스 홈 — 환경변수 미설정 시 dev 도메인 fallback. */
const CAMPUS_URL =
  import.meta.env.VITE_CAMPUS_URL || 'https://dev.1bpluschool.com'

/**
 * 이 **탭**에 토큰이 있는지. 아래 캠퍼스 link 조회를 **걸지 말지**의 스위치다.
 *
 * 토큰 없이 `/v2/auth/campus-link/me` 를 부르면 401 이 돌아오는데, 그 401 을
 * HTTP 클라이언트가 세션 만료로 보아 `/?session_expired=true` 로 **화면을 통째로
 * 옮긴다**(`custom-instance.ts` → `handleSessionExpired`). 안내가 전부인 이 페이지에서는
 * 사유를 읽기도 전에 날아간다는 뜻이라, 토큰이 없으면 아예 묻지 않는다.
 * (다행히 그 처리는 토큰을 실제로 붙여 보낸 요청에만 걸리므로, 묻지 않으면 안전하다.)
 *
 * 토큰은 sessionStorage(탭별)라 다른 탭의 로그인은 여기서 보이지 않는데, 그게 맞다 —
 * 토큰 없는 탭에서 `/` 로 가면 `_dashboard` 가 **랜딩(서비스 소개)** 을 그리므로,
 * 링크가 약속한 "돌아갈 대시보드" 대신 마케팅 페이지가 뜬다.
 *
 * `auth.getToken()` 은 저장소 예외를 일부러 삼키지 않는다(프라이빗 모드·사이트 데이터
 * 차단에서 throw). 이 페이지는 안내가 전부라 저장소 하나 때문에 흰 화면이 되면 사유조차
 * 못 읽으므로, 여기서만 감싸 "세션 없음" 으로 떨어뜨린다.
 */
function hasSessionInThisTab(): boolean {
  try {
    return auth.isAuthenticated()
  } catch {
    return false
  }
}

/**
 * reason 코드 → 사용자 카피.
 *
 * ⚠️ **이 페이지는 도달 출처를 세지 않는다.** 예전 주석은 reason 마다 출처를 열거해
 * "돌아갈 대시보드가 있는가" 를 추론했는데, 그 열거가 실제로 샜다 —
 * `shop_limit_exceeded` 는 캠퍼스 `confirm_install` 거절로도 오고(campus 가 준 reason 을
 * 백엔드 `handle_install_consent_callback` 이 그대로 `?reason=` 에 실어 보낸다),
 * 그 사용자에게는 **활성 link 가 없다.** 출처는 앞으로도 늘어난다.
 *
 * 그래서 링크 노출은 출처가 아니라 `_dashboard.tsx` 가드와 **같은 술어**
 * (`linked || exempt`)로 판정한다 — 아래 InstallNotEligiblePage 참조. 새 출처가 생겨도
 * 자동으로 맞고, "눌렀더니 방금 떠나온 화면으로 되돌아오는" 왕복이 생기지 않는다.
 *
 * 아래 (A)(B)(C) 는 **카피의 톤과 보조 안내**를 가르는 분류일 뿐이다.
 *
 * (A) **캠퍼스 자격 검증 거절** — `not_enrolled` / `course_ended` / `not_eligible`.
 *     캠퍼스 연동 콜백(`/v2/auth/cafe24/install-callback` granted=false)에서 온다.
 *     설치와 카페24 토큰은 그대로 살아 있으므로 "설치" 가 아니라 "이용" 으로 말한다.
 *     이 사용자에게 통하는 길은 하나뿐이라(카페24 관리자에서 앱 재실행 →
 *     `/v2/auth/cafe24/start` → OAuth → 캠퍼스 연동) 그 길만 안내한다.
 *
 * (B) **샵 한도 차단** — `shop_limit_exceeded`. 수강 자격은 있으나 강의별 샵 한도
 *     (온라인 1 / 오프라인 5)를 모두 소진한 경우다. 한도 숫자는 캠퍼스 API 에서 받고,
 *     카운트·초과 판정은 솔루션 백엔드 또는 캠퍼스 `confirm_install` 이 한다.
 *     이미 쓰고 있는 대시보드가 **있을 수도** 있어 링크 후보로 두지만, 실제 노출은
 *     조회 결과가 정한다(`mayHaveDashboard` 주석 참조).
 *
 * (C) **확인 불가** — `temporarily_unavailable`. 한도 확인이 일시적으로 실패한 경우
 *     (fail-closed). 거절이 아니라 판단 불가이므로 어느 쪽도 단정하지 않는 **중립**으로
 *     두고 재시도만 안내한다.
 */
const REASON_COPY: Record<
  string,
  {
    title: string
    body: string
    /** (A) 전용 — 카페24 관리자에서 앱 재실행이 유일한 재시도 경로임을 안내 */
    retryFromCafe24?: boolean
    /**
     * "돌아갈 대시보드가 있을 **수도** 있다" 는 후보 표시. 노출 결정이 아니다 —
     * 결정은 캠퍼스 link 조회(`linked || exempt`)가 한다. 이 플래그는 "물어볼 가치가
     * 있는 reason 인가" 만 가른다(아닌 reason 에서는 조회 자체를 걸지 않는다).
     */
    mayHaveDashboard?: boolean
  }
> = {
  // ── (A) 캠퍼스 자격 검증 거절 — 설치는 유지, 이용만 불가 ──
  not_enrolled: {
    title: '수강 중인 강의가 없어요',
    body: '본 솔루션은 자사몰 사관학교 수강생 전용 도구예요. 자사몰 사관학교에서 강의 수강을 시작한 뒤 캠퍼스 계정을 다시 연결해주세요. 설치한 앱은 그대로 유지돼요.',
    retryFromCafe24: true,
  },
  course_ended: {
    title: '수강 기간이 종료된 강의예요',
    body: '솔루션 이용 권한은 수강 중인 학생에게만 부여돼요. 새 강의를 신청한 뒤 캠퍼스 계정을 다시 연결해주세요. 설치한 앱은 그대로 유지돼요.',
    retryFromCafe24: true,
  },
  not_eligible: {
    title: '이용 권한을 확인할 수 없어요',
    body: '현재 자사몰 사관학교 캠퍼스 계정으로는 솔루션 이용 권한이 확인되지 않았어요. 수강 중인 강의가 있는지 자사몰 사관학교에서 확인해주세요. 설치한 앱은 그대로 유지돼요.',
    retryFromCafe24: true,
  },

  // ── (B) 샵 한도 차단 — 샵 추가만 막혔고, 쓰던 대시보드는 있을 수 있다 ──
  // 본문의 "이미 이용 중인 샵은 그대로 사용할 수 있어요" 는 어느 출처에서도 참이다
  // (거절된 것은 이번 샵뿐이고 기존 샵의 이용은 건드리지 않는다). 출처에 따라 갈리는
  // 것은 **링크의 약속**뿐이라 그쪽만 조회로 판정한다.
  shop_limit_exceeded: {
    title: '이용 가능한 샵 수를 모두 사용했어요',
    body: '현재 수강 중인 과정에서 이용할 수 있는 샵 수를 모두 사용했어요. 기존에 등록한 샵을 정리하거나, 샵을 더 추가할 수 있는 과정인지 자사몰 사관학교에서 확인해주세요. 이미 이용 중인 샵은 그대로 사용할 수 있어요.',
    mayHaveDashboard: true,
  },

  // ── (C) 확인 불가 — 거절이 아니므로 중립 ──
  temporarily_unavailable: {
    title: '잠시 후 다시 시도해주세요',
    body: '이용 권한을 확인하는 중 일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.',
  },
}

function InstallNotEligiblePage() {
  const { reason } = Route.useSearch()
  const copy = REASON_COPY[reason] ?? REASON_COPY.not_eligible

  // 조회를 거는 조건 둘. 링크 후보가 아닌 reason 에서는 물어볼 이유가 없고,
  // 토큰이 없으면 물으면 **안 된다**(hasSessionInThisTab 주석 — 401 이 이 화면을 날린다).
  const linkQueryEnabled = !!copy.mayHaveDashboard && hasSessionInThisTab()

  // 토큰이 **있는데 죽어 있는** 경우의 방어. 이 페이지는 `_dashboard` 레이아웃 밖이라
  // 인증 초기화 표시를 켜주는 곳이 없는데, 만료된 토큰으로 조회하면 401 →
  // `handleSessionExpired` → `/?session_expired=true` 로 **사유를 읽기도 전에** 화면이
  // 바뀐다. 초기화 구간으로 표시해 그 이동만 막는다 — 토큰 정리는 사용자가 `/` 로 갈 때
  // `_dashboard` 가 한다(401 일 때만 버리는 그 분기).
  //
  // 조회 훅보다 **먼저** 선언한다. 이펙트는 선언 순서대로 돌므로 이렇게 둬야
  // react-query 가 요청을 띄우기 전에 표시가 켜진다. 해제는 cleanup 이 짝을 맞춘다
  // (`setAuthBootstrapping` 은 불리언이 아니라 카운터라 StrictMode 이중 실행에도
  //  어긋나지 않는다).
  //
  // ⚠️ 남는 구멍 하나: 401 본문이 `cafe24_reauth_required` 면 이 표시와 무관하게
  // `/cafe24-reauth` 로 간다(`handleCafe24ReauthRequired` 는 초기화 표시를 보지 않는다).
  // 그 사용자는 어차피 대시보드 자체를 못 여는 상태이고 재연동 안내가 더 급해서 그대로 둔다.
  useEffect(() => {
    if (!linkQueryEnabled) return
    setAuthBootstrapping(true)
    return () => setAuthBootstrapping(false)
  }, [linkQueryEnabled])

  const { data: meRaw, isSuccess } = useGetCampusLinkForCurrentShop({
    query: { enabled: linkQueryEnabled },
  })
  const me = (meRaw ?? null) as CampusLinkMeResponse | null

  // **fail-closed** — 조회가 아직 안 왔거나 실패했으면 링크를 숨긴다.
  // `_dashboard.tsx` 가드는 같은 상황에서 fail-open 인데, 이 비대칭이 맞다:
  // 거기서 틀리면 멀쩡한 고객이 대시보드를 못 쓰고(불편), 여기서 틀리면 **눌러도
  // 되돌아오는 거짓 약속**이 된다. 링크가 없는 건 불편이지만 거짓말은 아니다.
  const showBackToDashboard = isSuccess && !!me && (!!me.linked || !!me.exempt)

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4 py-8">
      <div className="max-w-lg w-full bg-surface-lowest rounded-2xl border border-border p-6 sm:p-8">
        {/* 헤더 */}
        <div className="text-center mb-6">
          <img
            src="/images/onebplus_logo.svg"
            alt="자사몰 사관학교 솔루션"
            className="mx-auto mb-4 h-10"
          />
          <h1 className="text-lg font-bold text-on-surface mb-2">
            {copy.title}
          </h1>
          <p className="text-sm text-on-surface-variant leading-relaxed">
            {copy.body}
          </p>
        </div>

        {/* CTA — 자사몰 사관학교 캠퍼스 BI 메인 컬러 (#ff5757) — campus 의 globals.css 와 동기화.
            사용자가 가게 될 곳(캠퍼스)의 브랜드 컬러를 그대로 사용해 "어디로 가는 버튼인지"
            시각적으로 명시. 솔루션 design token 대신 raw hex 사용.
            모바일 터치 타겟 확보를 위해 최소 높이 44px. */}
        <a
          href={CAMPUS_URL}
          className="flex items-center justify-center gap-2 w-full min-h-[44px] rounded-lg bg-[#ff5757] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#ff5757]/90 transition-colors"
        >
          자사몰 사관학교 이동하기
          <ArrowRight className="h-4 w-4" />
        </a>

        {/* (A) 계열 — 재시도 경로 안내. 위 (A) 주석 참조: 이 사용자에게 통하는 길은
            카페24 관리자에서 앱을 다시 실행하는 것 하나뿐이다. */}
        {copy.retryFromCafe24 && (
          <p className="mt-4 text-center text-xs text-on-surface-variant leading-relaxed">
            연동을 다시 시도하려면 카페24 관리자에서 앱을 다시 실행해주세요.
          </p>
        )}

        {/* 돌아갈 대시보드가 **실재할 때만** 노출. 판정 술어가 `_dashboard.tsx` 가드와
            같아서, 눌렀다가 가드에 걸려 이 화면으로 되돌아오는 왕복이 생기지 않는다. */}
        {showBackToDashboard && (
          <Link
            to="/"
            className="mt-3 flex items-center justify-center gap-1.5 w-full min-h-[44px] rounded-lg px-4 py-2.5 text-sm text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            대시보드로 돌아가기
          </Link>
        )}
      </div>
    </div>
  )
}
